import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Puzzle, Check, Loader2, Lock, Sparkles, Gift, Star, ArrowUpRight,
} from "lucide-react";
import FortuneWheelDisk from "@/components/FortuneWheelDisk";

const inr2 = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inr0 = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN");

const USE_LINKS = {
  digital_product: { to: "/admin/products/new", label: "Create digital product", testId: "addon-use-digital" },
  ads_tool: { to: "/admin/ads", label: "Configure Ads Tool", testId: "addon-use-ads" },
  fortune_wheel: { to: "/admin/addons/fortune-wheel", label: "Configure wheel", testId: "addon-use-fortune_wheel" },
  single_product: { to: "/admin/addons/single-product", label: "Configure landing page", testId: "addon-use-single_product" },
  customer_refer: { to: "/admin/addons/refer-earn", label: "Configure Refer and Earn", testId: "addon-use-customer_refer" },
  custom_form: { to: "/admin/addons/custom-form", label: "Manage forms", testId: "addon-use-custom_form" },
  multi_currency: { to: "/admin/addons/multi-currency", label: "Configure countries", testId: "addon-use-multi_currency" },
  partial_payments: { to: "/admin/addons/partial-payments", label: "Configure Partial Payments", testId: "addon-use-partial_payments" },
  copyright_removal: { to: "/admin/addons/copyright-removal", label: "Configure footer credit", testId: "addon-use-copyright_removal" },
  firebase_otp: { to: "/admin/addons/firebase-otp", label: "Configure Firebase OTP", testId: "addon-use-firebase_otp" },
  custom_print: { to: "/admin/products", label: "Set up Custom Print on a product", testId: "addon-use-custom_print" },
  car_parts: { to: "/admin/products", label: "Add Make / Model / Year / Trim on a product", testId: "addon-use-car_parts" },
  gokwik_checkout: { to: "/admin/addons/gokwik-checkout", label: "Configure Gokwik Checkout", testId: "addon-use-gokwik_checkout" },
  fastrr_checkout: { to: "/admin/addons/fastrr-checkout", label: "Configure Fastrr Checkout", testId: "addon-use-fastrr_checkout" },
  timeslot_checkout: { to: "/admin/addons/timeslot-checkout", label: "Configure Timeslot Checkout", testId: "addon-use-timeslot_checkout" },
  ai_store_manager: { to: "/admin/addons/ai-store-manager", label: "Configure AI Store Manager", testId: "addon-use-ai_store_manager" },
  store_blog: { to: "/admin/blogs", label: "Manage blogs", testId: "addon-use-store_blog" },
  ondemand_areas: { to: "/admin/ondemand/areas", label: "Set pincodes and delivery messages", testId: "addon-use-ondemand_areas" },
  ondemand_delivery: { to: "/admin/ondemand/riders", label: "Create delivery boy accounts", testId: "addon-use-ondemand_delivery" },
  build_your_box: { to: "/admin/build-boxes", label: "Create a box", testId: "addon-use-build_your_box" },
  abp_capabilities: { to: "/admin/abp", label: "Set up mixtures", testId: "addon-use-abp_capabilities" },
  contact_page_form: { to: "/admin/contact-form", label: "Build the contact form", testId: "addon-use-contact_page_form" },
  course_page_maker: { to: "/admin/course-pages", label: "Open Course Page", testId: "addon-use-course_page_maker" },
  product_page_enhancer: { to: "/admin/products", label: "Add badges on a product", testId: "addon-use-product_page_enhancer" },
  per_product_shipping: { to: "/admin/products", label: "Set a shipping price on a product", testId: "addon-use-per_product_shipping" },
  author_pages: { to: "/admin/authors", label: "Manage authors", testId: "addon-use-author_pages" },
  zeplo_classic: { to: "/admin/ondemand/areas", label: "Set Zeplo service areas", testId: "addon-use-zeplo_classic" },
  invoice_maker: { to: "/admin/addons/invoice-maker", label: "Open Invoice Maker", testId: "addon-use-invoice_maker" },
  cosmos_design_enhancer: { to: "/admin/products", label: "Edit Cosmos product extras", testId: "addon-use-cosmos_design_enhancer" },
};

const THEMES = {
  amber: "from-amber-300 via-orange-300 to-rose-200",
  sky: "from-sky-200 via-indigo-100 to-violet-200",
  violet: "from-violet-300 via-fuchsia-200 to-pink-200",
  emerald: "from-emerald-200 via-teal-100 to-cyan-100",
  rose: "from-rose-200 via-pink-100 to-amber-100",
  slate: "from-slate-200 via-slate-100 to-white",
};

const THEME_BY_ID = {
  fortune_wheel: "amber",
  digital_product: "sky",
  ads_tool: "violet",
  customer_refer: "emerald",
  custom_form: "rose",
  multi_currency: "sky",
  single_product: "slate",
  partial_payments: "emerald",
  copyright_removal: "slate",
  firebase_otp: "amber",
  custom_print: "sky",
  car_parts: "slate",
  gokwik_checkout: "emerald",
  fastrr_checkout: "sky",
  timeslot_checkout: "amber",
  ai_store_manager: "violet",
  store_blog: "sky",
  ondemand_areas: "violet",
  ondemand_delivery: "emerald",
  build_your_box: "sky",
  abp_capabilities: "emerald",
  contact_page_form: "sky",
  course_page_maker: "violet",
  product_page_enhancer: "rose",
  per_product_shipping: "emerald",
  author_pages: "rose",
  zeplo_classic: "violet",
  cosmos_design_enhancer: "sky",
};

function compactCount(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace(/\.0$/, "")}k`;
  return String(v);
}

function Stars({ value }) {
  const n = Number(value) || 0;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(n) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
      ))}
      <span className="ml-1 text-xs font-semibold text-slate-800">{n ? n.toFixed(1) : "—"}</span>
    </span>
  );
}

function themeOf(a) {
  return THEMES[a.theme] || THEMES[THEME_BY_ID[a.id]] || THEMES.slate;
}

function MiniArt({ addon }) {
  if (addon.id === "fortune_wheel") {
    return (
      <div className="flex items-center justify-center h-full">
        <FortuneWheelDisk
          slices={[
            { label: "10%", color: "#E8D5C4" }, { label: "₹750", color: "#C9A07A" },
            { label: "15%", color: "#E8D5C4" }, { label: "₹1k", color: "#C9A07A" },
            { label: "Try", color: "#8B3A4A" }, { label: "₹500", color: "#E8D5C4" },
          ]}
          size={132}
          hubLabel="SPIN"
          testId="addon-featured-wheel-art"
        />
      </div>
    );
  }
  if (addon.image_url) {
    return <img src={mediaUrl(addon.image_url)} alt="" className="w-full h-full object-cover" />;
  }
  return (
    <div className="h-full flex items-center justify-center">
      <div className="w-16 h-16 rounded-2xl bg-white/70 shadow-sm flex items-center justify-center">
        <Puzzle className="w-7 h-7 text-slate-500" />
      </div>
    </div>
  );
}

export default function AdminAddons() {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [sort, setSort] = useState("popular");
  const [params, setParams] = useSearchParams();
  const loc = useLocation();

  const loadList = async () => {
    try {
      const { data: d } = await api.get("/merchant/addons");
      setData(d);
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  useEffect(() => {
    const cfOrderId = params.get("cf_order_id");
    const run = async () => {
      if (cfOrderId) {
        setVerifying(true);
        try {
          const { data: v } = await api.get(`/merchant/addons/verify/${cfOrderId}`);
          if (v.active) toast.success("Payment successful — addon unlocked for 1 year");
          else toast.error("Payment not completed yet. If you paid, it will unlock shortly.");
        } catch (e) {
          toast.error(extractErr(e));
        } finally {
          params.delete("cf_order_id");
          setParams(params, { replace: true });
          setVerifying(false);
        }
      }
      await loadList();
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const id = (loc.hash || "").replace("#", "");
    if (!id || !data) return;
    const t = setTimeout(() => {
      document.getElementById(`addon-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    return () => clearTimeout(t);
  }, [loc.hash, data]);

  const buy = async (addonId) => {
    setBusyId(addonId);
    try {
      const { data: d } = await api.post(`/merchant/addons/${addonId}/checkout`);
      const cashfree = await load({ mode: d.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setBusyId(null);
    }
  };

  const buyBundle = async () => {
    setBusyId("all_addons");
    try {
      const { data: d } = await api.post("/merchant/addons/bundle/checkout");
      const cashfree = await load({ mode: d.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: d.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setBusyId(null);
    }
  };

  const setEnabled = async (addonId, enabled) => {
    const prev = data;
    setData((d) => ({
      ...d,
      items: (d.items || []).map((a) => a.id === addonId
        ? { ...a, entitlement: { ...(a.entitlement || {}), enabled } }
        : a),
    }));
    setBusyId(`toggle-${addonId}`);
    try {
      const { data: r } = await api.put(`/merchant/addons/${addonId}/enabled`, { enabled });
      const disabled = r.disabled_ids || [];
      setData((d) => ({
        ...d,
        items: (d.items || []).map((a) => {
          if (a.id === addonId) return { ...a, entitlement: r.entitlement || { ...(a.entitlement || {}), enabled } };
          if (disabled.includes(a.id)) return { ...a, entitlement: { ...(a.entitlement || {}), enabled: false } };
          return a;
        }),
      }));
      if (enabled && disabled.length) {
        toast.success("Addon switched on. The other checkout addon was switched off.");
      } else {
        toast.success(enabled ? "Addon switched on" : "Addon switched off");
      }
    } catch (e) {
      setData(prev);
      toast.error(extractErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const items = data?.items || [];
  const featured = useMemo(
    () => items.filter((a) => a.is_featured).sort((a, b) => (a.featured_rank || 0) - (b.featured_rank || 0)),
    [items],
  );
  const listed = useMemo(() => {
    const xs = [...items];
    if (sort === "price") xs.sort((a, b) => (a.price || 0) - (b.price || 0));
    else xs.sort((a, b) => (b.stores_using || 0) - (a.stores_using || 0) || (b.rating_avg || 0) - (a.rating_avg || 0));
    return xs;
  }, [items, sort]);

  if (!data) {
    return (
      <div className="flex items-center gap-2 text-slate-500" data-testid="addons-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const bundle = data.bundle || {};
  const bundleActive = Boolean(bundle.entitlement?.active);
  const headlinePrice = Math.round(Number(bundle.price || 0));
  const savePct = bundle.list_price > bundle.price
    ? Math.round((1 - Number(bundle.price) / Number(bundle.list_price)) * 100)
    : 0;

  const actions = (a, { compact } = {}) => {
    const ent = a.entitlement || {};
    const owned = Boolean(ent.active);
    const on = Boolean(ent.enabled);
    const use = USE_LINKS[a.id];
    return (
      <div className={`flex flex-wrap items-center gap-2 ${compact ? "" : "justify-end"}`}>
        {owned && (
          <div className="flex items-center gap-2 mr-auto sm:mr-0" data-testid={`addon-switch-row-${a.id}`}>
            <Label htmlFor={`addon-switch-${a.id}`} className="text-xs text-slate-500">Use on store</Label>
            <Switch
              id={`addon-switch-${a.id}`}
              checked={on}
              disabled={busyId === `toggle-${a.id}`}
              onCheckedChange={(v) => setEnabled(a.id, v)}
              data-testid={`addon-switch-${a.id}`}
            />
          </div>
        )}
        {owned && use && (
          <Link to={use.to}>
            <Button size="sm" variant="outline" className="rounded-full" data-testid={use.testId}>{use.label}</Button>
          </Link>
        )}
        <Button
          size={compact ? "sm" : "default"}
          disabled={busyId === a.id || !data.cashfree_configured}
          onClick={() => buy(a.id)}
          className={`rounded-full ${owned ? "bg-slate-900 hover:bg-slate-800" : "bg-slate-950 hover:bg-black"} text-white`}
          data-testid={`addon-buy-${a.id}`}
        >
          {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
            owned ? "Extend" : <>Install <ArrowUpRight className="w-3.5 h-3.5 ml-1" /></>
          )}
        </Button>
      </div>
    );
  };

  const statusBadge = (a) => {
    const ent = a.entitlement || {};
    const owned = Boolean(ent.active);
    if (owned && ent.enabled) {
      return <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid={`addon-active-${a.id}`}><Check className="w-3 h-3 mr-1" /> On</Badge>;
    }
    if (owned) return <Badge variant="secondary" data-testid={`addon-off-${a.id}`}>Off</Badge>;
    return <Badge variant="secondary"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>;
  };

  const BundleCard = ({ className = "" }) => (
    <div
      className={`relative overflow-hidden rounded-3xl text-white p-6 shadow-xl shadow-slate-900/20 ${className}`}
      data-testid="addon-bundle-banner"
      style={{ background: "linear-gradient(165deg, #111827 0%, #1f2937 55%, #0f172a 100%)" }}
    >
      {savePct > 0 && (
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-semibold tracking-wide px-3 py-1 border border-emerald-400/20">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIMITED TIME · SAVE {savePct}%
        </div>
      )}
      <h2 className="font-display text-2xl font-bold mt-3">All Apps Bundle</h2>
      <p className="text-sm text-slate-300 mt-1">
        Buy all addons at discount price of just Rs.{headlinePrice.toLocaleString("en-IN")}
      </p>
      <div className="mt-4 flex items-end gap-2">
        <div className="text-4xl font-display font-bold tracking-tight">₹{inr0(bundle.total || bundle.price)}</div>
        <div className="pb-1 text-sm text-slate-400">/year{bundle.gst_percent ? " incl. GST" : ""}</div>
      </div>
      {bundle.list_price > bundle.price && (
        <div className="text-sm text-slate-500 line-through mt-0.5">₹{inr2(bundle.list_price)}/year individually</div>
      )}
      <ul className="mt-5 space-y-2 text-sm text-slate-200">
        <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {bundle.addon_count || items.length} premium apps included</li>
        <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Unlocks every App Store app for 1 year</li>
        <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Switch each app on or off anytime</li>
        <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> Extend later — no extra setup</li>
      </ul>
      {bundleActive ? (
        <div className="mt-6 space-y-2">
          <Badge className="bg-white text-slate-900 border-0"><Check className="w-3 h-3 mr-1" /> Unlocked</Badge>
          {bundle.entitlement?.expires_at && (
            <p className="text-xs text-slate-400">Until {new Date(bundle.entitlement.expires_at).toLocaleDateString("en-IN")}</p>
          )}
          <Button
            disabled={busyId === "all_addons" || !data.cashfree_configured}
            onClick={buyBundle}
            className="w-full h-11 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-semibold"
            data-testid="addon-extend-bundle"
          >
            {busyId === "all_addons" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Extend 1 year"}
          </Button>
        </div>
      ) : (
        <Button
          disabled={busyId === "all_addons" || !data.cashfree_configured}
          onClick={buyBundle}
          className="mt-6 w-full h-11 rounded-xl bg-white text-slate-900 hover:bg-slate-100 font-semibold"
          data-testid="addon-buy-bundle"
        >
          {busyId === "all_addons" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get Bundle Deal <ArrowUpRight className="w-4 h-4 ml-1" /></>}
        </Button>
      )}
      <p className="text-[11px] text-slate-500 mt-3 text-center">Yearly billing · {bundle.gst_percent || 18}% GST extra on listed base</p>
      <div className="mt-5 flex -space-x-2">
        {items.slice(0, 6).map((a) => (
          <div key={a.id} className="w-8 h-8 rounded-full bg-slate-700 ring-2 ring-slate-900 overflow-hidden flex items-center justify-center text-[10px] font-bold">
            {a.image_url ? <img src={mediaUrl(a.image_url)} alt="" className="w-full h-full object-cover" /> : (a.name || "?").slice(0, 1)}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-[1180px]">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
          <Puzzle className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">App Store</h1>
          <p className="text-slate-500 text-sm mt-1">
            Unlock extras that enhance your storefront. Each app is billed yearly (18% GST extra) or granted by support.
          </p>
        </div>
      </div>

      {verifying && (
        <div className="mb-4 rounded-xl border bg-white p-4 flex items-center gap-2 text-slate-600" data-testid="addons-verifying">
          <Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…
        </div>
      )}

      <div className="lg:hidden mb-6">
        <BundleCard />
      </div>

      <div className="flex items-start gap-8">
        <div className="flex-1 min-w-0 space-y-8">
          {featured.length > 0 && (
            <section data-testid="addon-featured-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h2 className="font-display text-lg font-bold text-slate-900">Featured Apps</h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">Handpicked by MeraStartup team</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {featured.map((a) => (
                  <div
                    key={a.id}
                    className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${themeOf(a)} border border-white/60 shadow-sm ${loc.hash === `#${a.id}` ? "ring-2 ring-violet-500" : ""}`}
                    data-testid={`addon-featured-${a.id}`}
                  >
                    {a.badge && (
                      <div className="absolute right-3 top-3 z-10 rounded-md bg-slate-950 text-white text-[10px] font-bold tracking-wide px-2 py-1">
                        {a.badge}
                      </div>
                    )}
                    <div className="h-40 px-4 pt-5">
                      <MiniArt addon={a} />
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-display font-bold text-lg text-slate-900">{a.name}</div>
                          <p className="text-sm text-slate-500">{a.tagline || a.description}</p>
                        </div>
                        {statusBadge(a)}
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                        <Stars value={a.rating_avg} />
                        <span>{compactCount(a.stores_using)} stores</span>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="font-mono-nums font-bold text-slate-900">₹{inr2(a.price)}<span className="text-xs font-normal text-slate-500">/yr</span></div>
                        {actions(a, { compact: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-bold text-slate-900">All Apps</h2>
                <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{listed.length}</span>
              </div>
              <select
                className="h-9 rounded-lg border border-slate-200 bg-white text-sm px-3"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="addon-sort"
              >
                <option value="popular">Sorted by Popular</option>
                <option value="price">Sorted by Price</option>
              </select>
            </div>
            {listed.length === 0 ? (
              <div className="rounded-2xl border bg-white p-8 text-center text-slate-500 text-sm">No addons are available yet.</div>
            ) : (
              <div className="space-y-3">
                {listed.map((a) => {
                  const ent = a.entitlement || {};
                  const owned = Boolean(ent.active);
                  return (
                    <div
                      key={a.id}
                      id={`addon-${a.id}`}
                      className={`rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 ${loc.hash === `#${a.id}` ? "ring-2 ring-violet-500" : ""}`}
                      data-testid={`addon-card-${a.id}`}
                    >
                      <div className="flex gap-4">
                        <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${themeOf(a)} overflow-hidden shrink-0 flex items-center justify-center`}>
                          {a.image_url ? <img src={mediaUrl(a.image_url)} alt="" className="w-full h-full object-cover" /> : <Gift className="w-7 h-7 text-slate-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-display font-bold text-lg text-slate-900">{a.name}</div>
                            {a.badge && <span className="text-[10px] font-bold tracking-wide bg-slate-950 text-white rounded px-1.5 py-0.5">{a.badge}</span>}
                            {a.require_theme === "cosmos" ? <span className="text-[10px] font-bold tracking-wide bg-sky-100 text-sky-800 rounded px-1.5 py-0.5" data-testid="addon-cosmos-only">Cosmos theme only</span> : null}
                            {statusBadge(a)}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{compactCount(a.stores_using)} stores using</p>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{a.tagline || a.description}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                            <Stars value={a.rating_avg} />
                            {a.rating_count ? <span>{a.rating_count} reviews</span> : null}
                            <span className="font-mono-nums font-bold text-slate-900 text-sm">₹{inr2(a.price)}<span className="font-normal text-slate-500">/yr + GST</span></span>
                          </div>
                          {owned && ent.expires_at && (
                            <div className="text-xs text-emerald-700 mt-1">Unlocked until {new Date(ent.expires_at).toLocaleDateString("en-IN")}</div>
                          )}
                          <div className="mt-3">{actions(a)}</div>
                          {!data.cashfree_configured && !owned && (
                            <p className="text-xs text-amber-700 mt-2">Online checkout is not configured. Ask support to unlock this addon for your store.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <aside className="hidden lg:block w-[320px] shrink-0">
          <div className="sticky top-4 space-y-4">
            <BundleCard />
            <div className="rounded-2xl bg-slate-100 p-4">
              <div className="text-sm font-semibold text-slate-900 mb-3">Why MeraStartup?</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl bg-white p-3">
                  <div className="text-xl font-display font-bold text-slate-900">4.9/5</div>
                  <div className="text-[11px] text-slate-500">Average rating</div>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <div className="text-xl font-display font-bold text-slate-900">1 yr</div>
                  <div className="text-[11px] text-slate-500">Unlock term</div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
